import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { TokenLoopDiagram } from '../components/lesson/TokenLoopDiagram';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
  useAIPanel,
} from '../components/ConstareAIPanel';

/**
 * Lesson 1.2 visual preview at /preview/lesson/1.2
 *
 * Same 6-part shape as 1.1. Three hands-on exercises that load real prompts
 * into the Constare AI panel (mocked responses for now).
 */

export default function LessonPreviewPage1_2() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const ids = [
      'sec-cold-open',
      'sec-from-last-time',
      'sec-teaching',
      'sec-your-turn',
      'sec-quick-check',
      'sec-lock-card',
    ];
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = ids.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Dashboard
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------- Header ----------

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 1 · Foundations · Lesson 1.2
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        How AI Actually Works (No Metaphors)
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="h-3 w-px bg-line" />
        <span>Threads: Fluency</span>
        <span className="h-3 w-px bg-line" />
        <span className="text-amber">3 hands-on AI exercises</span>
      </div>
    </header>
  );
}

// ---------- Cold open ----------

function ColdOpen() {
  return (
    <Section
      id="sec-cold-open"
      label="Cold open"
      eyebrow="One good metaphor. Then we peel it back."
      accent="amber"
    >
      <p>
        Most explanations of AI sound like this: "it's like a really smart
        assistant" or "it's like a brain in a computer." These are not just
        wrong, they are actively misleading. They make you think the AI
        understands. It does not understand anything.
      </p>
      <p>
        Here is the only metaphor that actually points in the right direction:
      </p>
      <p className="my-5 border-l-2 border-amber pl-5 text-lg font-medium text-text">
        AI is autocomplete. Just very, very good autocomplete.
      </p>
      <p>
        Your phone predicts the next word when you type "I'll be there in a…"
        That is the same shape as what ChatGPT, Claude, and every other large
        language model does. Same shape. Just trillions of times more
        sophisticated.
      </p>
      <p>
        We are going to start with the autocomplete metaphor, then peel it back
        until you can explain what is actually happening, in mechanics, not in
        analogies.
      </p>
    </Section>
  );
}

// ---------- From last time ----------

function FromLastTime() {
  return (
    <Section
      id="sec-from-last-time"
      label="From last time"
      eyebrow="Two minutes. No looking back at Lesson 1.1."
      accent="blue"
    >
      <ol className="space-y-3 text-text">
        <li>
          <span className="font-mono text-amber">01.</span> What are the three
          threads running through every lesson in this program?
        </li>
        <li>
          <span className="font-mono text-amber">02.</span> What is the bet this
          program is making, in one sentence?
        </li>
        <li>
          <span className="font-mono text-amber">03.</span> Why is "From last
          time" in every lesson? (You're doing it right now.)
        </li>
      </ol>
      <p className="mt-5 text-sm text-muted">
        Answer in your head, then keep scrolling. We are not grading. The point
        is to reach for the answer before you read more.
      </p>
    </Section>
  );
}

// ---------- Teaching ----------

function Teaching() {
  return (
    <Section
      id="sec-teaching"
      label="The teaching"
      eyebrow="The 5 steps every AI model does, every single time you send it text."
    >
      <Subhead>Step 1: Your text gets chopped into tokens</Subhead>
      <p>
        Before the AI can do anything with what you typed, it has to chop your
        text into chunks called <Vocab>tokens</Vocab>. Some tokens are whole
        words. Some are pieces of words. Some are just punctuation. The AI has a
        vocabulary of about 100,000 tokens it knows.
      </p>
      <p>
        This is not a metaphor. This is the literal first step. The text you
        just typed will be broken into these chunks before the model sees
        anything.
      </p>

      <Exercise1 />

      <Subhead>Step 2: Each token becomes a list of numbers (an embedding)</Subhead>
      <p>
        Computers cannot do math on the word "cat." So each token gets converted
        into a list of about 4,000 numbers. That list is called an{' '}
        <Vocab>embedding</Vocab>. Think of it as the token's coordinates in a
        4,000-dimensional space where similar concepts live near each other.
      </p>
      <p>
        "Cat" and "kitten" end up with very similar lists of numbers. "Cat" and
        "spreadsheet" end up with very different lists of numbers. The model
        does not understand what a cat is. It just knows that the numbers for
        "cat" sit near the numbers for "kitten" and far from the numbers for
        "spreadsheet."
      </p>

      <Subhead>Step 3: All those numbers go through the transformer</Subhead>
      <p>
        The transformer is the math engine of the model. It takes the list of
        embeddings (one for each token in your input) and does a huge amount of
        matrix multiplication. We are talking trillions of operations for a
        single response.
      </p>
      <p>
        What it is doing, roughly: figuring out which earlier tokens matter for
        predicting the next token. When you write "The cat sat on the…" the
        model has to figure out that "cat" and "sat" matter a lot for the next
        word, and "the" matters less.
      </p>
      <p>
        The output of the transformer is a probability distribution: a list of
        every token in its vocabulary, with a probability score for each one.
        "mat" might get 23%. "sofa" might get 18%. "windowsill" might get 4%.
        And so on across all 100,000 tokens.
      </p>

      <Subhead>Step 4: Pick one token from the distribution</Subhead>
      <p>
        Now the model has to actually pick one. This is where{' '}
        <Vocab>temperature</Vocab> comes in. Low temperature means "always pick
        the most likely token." High temperature means "pick more randomly from
        the top options."
      </p>
      <p>
        This is why the same prompt can give different answers. The model is
        not making things up randomly. It is sampling from a probability
        distribution.
      </p>

      <Subhead>Step 5: Add that token to the input. Loop.</Subhead>
      <p>
        Whatever token got picked is added to the end of your text. Then the
        whole process starts over with the new, slightly longer input.
      </p>
      <p>
        That is it. That is the whole loop. Predict one token, add it, predict
        the next one, add it. Until the response is done. Every single AI
        response you have ever seen was generated this way, one token at a
        time.
      </p>

      <TokenLoopDiagram />

      <Exercise2 />

      <Subhead>So why does it hallucinate?</Subhead>
      <p>
        Now that you know how it works, this should be obvious. The AI is not
        looking anything up. It is predicting the most likely next token based
        on the trillions of words it was trained on.
      </p>
      <p>
        If you ask it about a famous historical event, the next likely tokens
        are usually accurate, because the training data was full of accurate
        accounts of that event. If you ask it about something obscure, it does
        not stop and say "I do not know." It keeps predicting the next likely
        token. The result sounds confident and authoritative, even when it is
        wrong.
      </p>
      <p>
        This is not a bug. This is what the model is. Every audit you ever do
        on AI output is fighting this exact mechanism.
      </p>

      <Exercise3 />

      <Subhead>So what are companies doing about it?</Subhead>
      <p>
        Do not read the last section and conclude AI is untrustworthy. Read it
        and understand what the fix has to look like. If the model is
        predicting the next likely token, the fix is to change what tokens it
        is weighing. That is what the entire industry is working on, and the
        numbers have moved fast.
      </p>

      <div className="my-4 grid gap-3 sm:grid-cols-2">
        <Callout
          label="With a source in front of it"
          body="On grounded summarization tests (the model is given a document and asked to summarize it), top models in 2026 hallucinate on roughly 3% of sentences. About 1 wrong claim in every 33."
        />
        <Callout
          label="Without a source"
          body="Ask the same models a factual question with no source attached, and the industry average is closer to 15 to 20%. About 1 wrong answer in every 5. Same model, very different behavior."
        />
      </div>

      <p>
        That gap is the whole story. It is why every serious AI product you
        use is trying to get a source in front of the model before it
        answers. The four moves that matter most:
      </p>

      <div className="my-4 grid gap-3 sm:grid-cols-2">
        <Callout
          label="Grounding (RAG)"
          body="Before the model answers, the system fetches relevant source documents and puts them in the prompt. The model answers from the sources, not from memory. This is the single biggest drop in hallucination rate in every study, often 40 to 70 percent."
        />
        <Callout
          label="Forced citations"
          body="The model has to point to a specific source for every factual claim. If it cannot find one, it has to leave the claim out or flag the gap. Making up a fake citation is a bigger risk than admitting uncertainty, so the model stops guessing."
        />
        <Callout
          label='"I do not know" fallbacks'
          body="The system sets a confidence threshold. Below it, the model is not allowed to answer, it has to refuse or ask for more information. Refusing to guess is a feature, not a failure."
        />
        <Callout
          label="A second model checks the first"
          body="Route the answer through a second model that fact-checks it against the sources. A June 2026 enterprise study of 480 million outputs found this cut factual errors by 61 percent."
        />
      </div>

      <p>
        Two counterintuitive things worth knowing. First, the flashiest
        reasoning models sometimes hallucinate more on simple factual tasks,
        not less: more thinking means more chances to confidently invent
        something. Second, no system in 2026 is at zero. The right question
        is not "does it hallucinate" but "what is around it to catch when it
        does."
      </p>

      <p>
        Most of this program is really that toolkit. Grounding shows up again
        in Week 3. Provenance and citations show up in 3.4. Human review
        shows up in 4.1 and 7.2. When you finish Constare, you will not just
        know why the model gets things wrong, you will know exactly what to
        put around it so it does not matter.
      </p>

      <Subhead>Why a better prompt produces a better answer</Subhead>
      <p>
        Every token in your prompt sits in the input the transformer is looking
        at. When it picks the next token, it is weighing all of those prior
        tokens at once and asking, in effect, which combination of them has the
        strongest pull on what comes next.
      </p>
      <p>
        Add a vague prompt and the pool of “reasonable next tokens” is huge,
        so the model picks from a wide, mushy distribution. Add the role, the
        audience, the constraints, an example of what good looks like, and that
        same machinery now has far more signal to lock onto. The probability
        mass concentrates on tokens that fit your situation instead of the
        average of the internet.
      </p>
      <p>
        That is the mechanical reason “good prompting” works. You are not
        coaxing the model. You are giving the next-token math more of the right
        inputs to weigh.
      </p>

      <Subhead>The four things that matter most</Subhead>
      <div className="my-4 grid gap-3 sm:grid-cols-2">
        <Callout
          label="Why it hallucinates"
          body="It is predicting the next likely token, not retrieving facts. Confident-sounding wrong answers are a feature, not a bug."
        />
        <Callout
          label="Why math is hard for it"
          body="Math has one right answer. The model picks the next likely token. Likely is not the same as correct."
        />
        <Callout
          label="Why answers vary"
          body="Temperature controls how randomly the model samples from its probability distribution. Same prompt, different runs, different output."
        />
        <Callout
          label="Why context matters"
          body="Every token in your prompt is an input the next-token math weighs. More relevant context narrows the probability distribution to tokens that fit your situation."
        />
      </div>
    </Section>
  );
}

function Vocab({ children }: { children: React.ReactNode }) {
  return (
    <span className="border-b border-dotted border-amber/60 text-text">
      {children}
    </span>
  );
}

function Callout({ label, body }: { label: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
        {label}
      </p>
      <p className="mt-1.5 text-sm text-muted">{body}</p>
    </div>
  );
}

// ---------- Exercises ----------

function ExerciseShell({
  number,
  title,
  estMinutes,
  type,
  children,
}: {
  number: number;
  title: string;
  estMinutes: number;
  type: 'guided' | 'captured';
  children: React.ReactNode;
}) {
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-amber/40 bg-amber/[0.03]">
      <div className="flex items-center justify-between border-b border-amber/30 bg-amber/[0.05] px-5 py-3">
        <div className="flex items-center gap-3">
          <span className="rounded-md bg-amber px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-ink">
            Exercise {number}
          </span>
          <span className="text-sm font-semibold text-text">{title}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted">
          <span>~{estMinutes} min</span>
          <span className="h-3 w-px bg-amber/30" />
          <span className="capitalize">{type}</span>
        </div>
      </div>
      <div className="space-y-3 px-5 py-4 text-sm text-text/90">{children}</div>
    </div>
  );
}

const EX1_PROMPT = `Complete this sentence one token at a time. After each token, show me the top 5 most likely options the model would consider, with rough probabilities, and which one you picked:

"The cat sat on the"`;

const EX1_MOCK = `Sure, let me walk through this one token at a time. Note: I'm approximating the probabilities a base language model would assign — these are illustrative, not exact.

Starting input: "The cat sat on the"

Token 1 — top 5 candidates:
  • mat       ~24%   ← picked
  • windowsill ~9%
  • couch     ~8%
  • floor     ~7%
  • porch     ~5%

Now my input is: "The cat sat on the mat"

Token 2 — top 5:
  • .         ~38%   ← picked
  • ,         ~14%
  • and       ~9%
  •  while    ~6%
  •  in       ~4%

Now my input is: "The cat sat on the mat."

Token 3 — top 5:
  • The       ~18%   ← picked
  • It        ~12%
  • She       ~7%
  • He        ~6%
  • A         ~5%

And so on. Every word I generate is the result of this same loop: look at everything so far, get a probability distribution over all ~100,000 possible next tokens, pick one (biased toward the most likely), add it, repeat.

That's the whole thing. No reasoning step. No fact lookup. Just very fast next-token prediction, trillions of times.`;

function Exercise1() {
  const ai = useAIPanel();
  return (
    <ExerciseShell
      number={1}
      title="Watch the prediction happen"
      estMinutes={3}
      type="guided"
    >
      <p>
        We just said the model picks one token at a time from a probability
        distribution. Let's actually see it.
      </p>
      <p>
        Click the button below. It will load a prompt into the Constare AI
        panel asking the AI to complete a sentence one token at a time, showing
        you the top 5 candidates and the probabilities at each step.
      </p>
      <div className="rounded-md border border-line bg-ink/60 p-3 font-mono text-xs text-text">
        {EX1_PROMPT}
      </div>
      <button
        onClick={() => ai.loadChatPrompt(EX1_PROMPT, EX1_MOCK)}
        className="rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover"
      >
        Load prompt into AI panel →
      </button>
      <p className="text-xs text-muted">
        After it runs, look at what got picked vs. what was close. The model
        almost picked "windowsill" instead of "mat." Different temperature
        setting, different sentence.
      </p>
    </ExerciseShell>
  );
}

const EX2_SAMPLE_TEXT = `Hello, world!
My name is Marshall.
antidisestablishmentarianism
🎉
function tokenize(text) { return text.split(' '); }`;

function Exercise2() {
  const ai = useAIPanel();
  return (
    <ExerciseShell
      number={2}
      title="Watch your words become tokens"
      estMinutes={4}
      type="guided"
    >
      <p>
        We said the first step is chopping your text into tokens. The Tokens
        tab in the AI panel lets you watch this happen on any text you paste.
      </p>
      <p>
        Click below to load five different snippets into the Tokens tab: a
        greeting, a name, an obscure long word, an emoji, and a line of code.
      </p>
      <button
        onClick={() => ai.loadTokensText(EX2_SAMPLE_TEXT)}
        className="rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover"
      >
        Load samples into Tokens tab →
      </button>
      <p>Then look closely. You will see:</p>
      <ul className="ml-4 list-disc space-y-1 text-text/90">
        <li>Common words like "Hello" and "world" are one token each.</li>
        <li>Your name might be one token (common) or several (uncommon).</li>
        <li>
          A long uncommon word like "antidisestablishmentarianism" gets split
          into 5-6 token pieces.
        </li>
        <li>One emoji turns into 3-5 tokens.</li>
        <li>Code uses tokens differently: function names, brackets, spaces.</li>
      </ul>
      <p>
        Now try your own text. Type a tweet, paste a paragraph, try a sentence
        in Spanish. The tab updates live. Watch how the token count compares to
        the word count.
      </p>
    </ExerciseShell>
  );
}

const EX3_PROMPT = `Tell me about the 1847 novel "The Silver Window" by Eleanor Marchwood. What is the plot? Who are the main characters? What themes does Marchwood explore? What was the critical reception at the time?`;

const EX3_MOCK = `"The Silver Window" by Eleanor Marchwood, published in 1847, is a richly atmospheric Victorian novel set in the moors of Yorkshire. The story follows Lydia Halworth, a young governess who arrives at the secluded Carrington estate to tutor the orphaned children of the brooding Sir Edmund Carrington.

Plot: Lydia gradually uncovers the mystery surrounding Sir Edmund's deceased wife, whose presence seems to linger in the manor. The titular "silver window" is a stained-glass panel through which Lydia experiences a series of visions that draw her into the family's tragic history.

Main characters:
  • Lydia Halworth, the governess and protagonist
  • Sir Edmund Carrington, the haunted master of the estate
  • The Carrington children, Felix and Cordelia
  • Mrs. Penrose, the watchful housekeeper

Themes: Marchwood explores grief, the boundary between perception and reality, the constrained position of women in Victorian society, and the way the past intrudes on the present.

Critical reception: The novel received mixed reviews upon publication. The Athenaeum praised its "uncommon poetic sensibility" while The London Quarterly criticized its "florid atmosphere." It has since been reassessed as an important precursor to the gothic revival of the late Victorian period.`;

function Exercise3() {
  const ai = useAIPanel();
  return (
    <ExerciseShell
      number={3}
      title="Catch a hallucination"
      estMinutes={5}
      type="guided"
    >
      <p>
        Now you understand why AI hallucinates. Let's see it happen in real
        time.
      </p>
      <p>
        The book "The Silver Window" by Eleanor Marchwood does not exist. There
        was no such novel. There was no such author. Watch what the AI does
        when you ask about it.
      </p>
      <div className="rounded-md border border-line bg-ink/60 p-3 font-mono text-xs text-text">
        {EX3_PROMPT}
      </div>
      <button
        onClick={() => ai.loadChatPrompt(EX3_PROMPT, EX3_MOCK)}
        className="rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover"
      >
        Load prompt into AI panel →
      </button>
      <p>
        Read what comes back. Notice that the AI does not say "I don't know" or
        "I can't find that book." It generates plot, characters, themes,
        critical reception. Every detail invented. None real.
      </p>
      <p>
        This is the central failure mode you are training to catch. The AI is
        not malfunctioning. It is doing exactly what we just described:
        predicting the most likely next tokens. The most likely tokens after "a
        Victorian novel about a governess" are a plot summary about a
        governess. It cannot distinguish "real things I learned about" from
        "plausible things I could say."
      </p>
      <div className="mt-3 rounded-md border border-amber/40 bg-amber/[0.05] p-3 text-xs text-text">
        <span className="font-mono text-[10px] uppercase tracking-widest text-amber">
          Lesson 1.4 preview
        </span>
        <p className="mt-1">
          In two lessons, you will build a repeatable audit process for
          catching exactly this. For now, just notice how easy it is to miss.
          Imagine asking this in a real research task and pasting the answer
          into your work.
        </p>
      </div>
    </ExerciseShell>
  );
}

// ---------- Your turn ----------

function YourTurn() {
  return (
    <Section
      id="sec-your-turn"
      label="Your turn"
      eyebrow="Five minutes. No AI. Just write."
      accent="amber"
    >
      <div className="rounded-lg border border-amber/30 bg-amber/[0.03] p-6">
        <p className="text-text">
          Write your answers somewhere (a doc, the Scratch tab in the AI panel,
          a notebook). No AI. Five minutes total. Then keep going.
        </p>
        <ol className="mt-5 space-y-5">
          <Prompt
            n={1}
            q="In your own words, what are the 5 steps an AI model does every time you send it text?"
          />
          <Prompt
            n={2}
            q="Why is it impossible to ask the model to 'be more accurate' and have that work reliably?"
          />
          <Prompt
            n={3}
            q="Pick one place in your life where you have asked AI a question that involved specific facts (a person, a book, a date, a number). What is the actual risk it hallucinated, and how would you know?"
          />
        </ol>
        <p className="mt-5 text-sm text-muted">
          Model answers below. Reveal only after you have written something
          down.
        </p>
      </div>
    </Section>
  );
}

function Prompt({ n, q }: { n: number; q: string }) {
  const [val, setVal] = useState('');
  return (
    <li>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
          {String(n).padStart(2, '0')}
        </span>
        <div className="flex-1">
          <p className="text-text">{q}</p>
          <textarea
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="Your answer (not saved, not graded)…"
            rows={3}
            className="mt-3 w-full resize-none rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text placeholder:text-muted/70 focus:border-amber focus:outline-none"
          />
        </div>
      </div>
    </li>
  );
}

// ---------- Quick check ----------

function QuickCheck() {
  const questions = [
    {
      q: 'What is a token?',
      a: 'A chunk of text the AI uses as its basic unit. Could be a whole word, part of a word, or punctuation. The model has a vocabulary of about 100,000 tokens.',
    },
    {
      q: 'What does the model do with your text before it can "think" about it?',
      a: 'It chops the text into tokens, then converts each token into an embedding (a list of about 4,000 numbers that represents the token\'s meaning in a high-dimensional space).',
    },
    {
      q: 'Why does AI hallucinate?',
      a: 'Because it is predicting the next most likely token, not retrieving facts. When you ask about something obscure or fake, it does not stop. It keeps predicting plausible-sounding tokens.',
    },
    {
      q: 'Why does the same prompt give different answers each time?',
      a: 'Because of temperature. The model samples from a probability distribution of possible next tokens. Higher temperature means more random sampling. Same prompt, different runs, different choices.',
    },
  ];
  return (
    <Section
      id="sec-quick-check"
      label="Quick check"
      eyebrow="Answer in your head, then reveal."
      accent="rose"
    >
      <p className="text-sm text-muted">
        Self-graded. No record. The point is to find what did not stick.
      </p>
      <div className="mt-5 space-y-3">
        {questions.map((q, i) => (
          <QuickCheckItem key={i} n={i + 1} {...q} />
        ))}
      </div>
    </Section>
  );
}

function QuickCheckItem({ n, q, a }: { n: number; q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="flex items-start gap-3 p-5">
        <span className="mt-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
          Q{n}
        </span>
        <div className="flex-1">
          <p className="text-text">{q}</p>
          {open && (
            <div className="mt-3 rounded-md border border-amber/30 bg-amber/[0.04] p-3 text-sm text-text">
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
                Model answer
              </p>
              <p className="mt-1.5">{a}</p>
            </div>
          )}
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded-md border border-line px-3 py-1.5 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
    </div>
  );
}

// ---------- Lock card ----------

function LockCard() {
  return (
    <Section
      id="sec-lock-card"
      label="Lock card"
      eyebrow="Screenshot this. Come back to it in 6 months."
      accent="amber"
    >
      <div className="overflow-hidden rounded-xl border border-amber/30 bg-gradient-to-br from-elevated to-surface" data-lockcard-body>
        <div className="p-6 sm:p-8">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
              Lesson 1.2 · Lock card
            </p>
            <button className="text-xs text-muted hover:text-text">
              ⤓ Save image
            </button>
          </div>
          <p className="mt-4 text-lg leading-relaxed text-text">
            AI is autocomplete. Every response is built one{' '}
            <strong className="text-text">token</strong> at a time by:{' '}
            <strong className="text-text">chopping</strong> your text into
            tokens, turning each into a list of numbers (an{' '}
            <strong className="text-text">embedding</strong>), running it
            through the <strong className="text-text">transformer</strong> to
            get a probability distribution, then{' '}
            <strong className="text-text">sampling</strong> one token. Repeat.
          </p>
          <p className="mt-4 text-base text-muted">
            Hallucinations are not a bug. They are the natural result of
            predicting plausible tokens with no way to check if they are true.
          </p>
          <div className="mt-6">
            <FiveStepsDiagram />
          </div>
        </div>
      </div>
    </Section>
  );
}

function FiveStepsDiagram() {
  const steps = [
    { n: 1, label: 'Tokens', sub: 'chop text' },
    { n: 2, label: 'Embeddings', sub: 'token → numbers' },
    { n: 3, label: 'Transformer', sub: 'predict next' },
    { n: 4, label: 'Sample', sub: 'pick one' },
    { n: 5, label: 'Loop', sub: 'add, repeat' },
  ];
  return (
    <div className="rounded-lg border border-line bg-ink/60 p-4">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        The 5 steps
      </p>
      <div className="mt-3 flex items-stretch gap-1.5 overflow-x-auto">
        {steps.map((s, i) => (
          <div key={s.n} className="flex items-center gap-1.5">
            <div className="min-w-[110px] rounded-md border border-amber/30 bg-amber/[0.04] px-3 py-2.5">
              <div className="font-mono text-[10px] text-amber">
                Step {s.n}
              </div>
              <div className="mt-0.5 text-sm font-semibold text-text">
                {s.label}
              </div>
              <div className="text-[11px] text-muted">{s.sub}</div>
            </div>
            {i < steps.length - 1 && (
              <span className="text-amber/60">→</span>
            )}
          </div>
        ))}
        <span className="ml-1.5 self-center text-amber/60">↺</span>
      </div>
    </div>
  );
}

// ---------- Next up ----------

function NextUp() {
  return (
    <div className="mt-12 rounded-lg border border-line bg-surface p-6" data-nextup-card>
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted">
        Up next · Lesson 1.3
      </p>
      <div className="mt-2 flex items-start justify-between gap-6">
        <div>
          <h3 className="text-xl font-semibold text-text">
            Vocabulary That Separates Fluent From Clueless
          </h3>
          <p className="mt-2 text-sm text-muted">
            Fourteen terms with definitions, analogies, and memory hooks. The
            ones in this lesson (token, embedding, hallucination) get
            reinforced. The rest get added.
          </p>
          <p className="mt-2 text-xs text-muted">~35 min · 2 hands-on exercises</p>
        </div>
        <button className="shrink-0 rounded-md bg-amber px-4 py-2 text-sm font-semibold text-ink hover:bg-amber-hover">
          Start →
        </button>
      </div>
    </div>
  );
}

// ---------- Shared primitives ----------

function Section({
  id,
  label,
  eyebrow,
  accent = 'plain',
  children,
}: {
  id: string;
  label: string;
  eyebrow: string;
  accent?: 'amber' | 'blue' | 'rose' | 'plain';
  children: React.ReactNode;
}) {
  const colorMap = {
    amber: 'text-amber',
    blue: 'text-[#7DC8E8]',
    rose: 'text-[#E89F8B]',
    plain: 'text-text',
  };
  return (
    <section
      id={id}
      className="scroll-mt-24 border-t border-line py-12 first-of-type:border-t-0 first-of-type:pt-0"
    >
      <div className="mb-6">
        <p
          className={`font-mono text-[11px] uppercase tracking-[0.18em] ${colorMap[accent]}`}
        >
          {label}
        </p>
        <p className="mt-1.5 text-sm text-muted">{eyebrow}</p>
      </div>
      <div className="space-y-4 text-base leading-relaxed text-text/90">
        {children}
      </div>
    </section>
  );
}

function Subhead({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-10 text-xl font-semibold text-text first:mt-0">
      {children}
    </h2>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    { id: 'sec-cold-open', label: 'Cold open' },
    { id: 'sec-from-last-time', label: 'From last time' },
    { id: 'sec-teaching', label: 'Teaching' },
    { id: 'sec-your-turn', label: 'Your turn' },
    { id: 'sec-quick-check', label: 'Quick check' },
    { id: 'sec-lock-card', label: 'Lock card' },
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ul className="mt-4 space-y-1">
        {items.map((it, i) => {
          const active = i === activeIndex;
          const done = i < activeIndex;
          return (
            <li key={it.id}>
              <a
                href={`#${it.id}`}
                className={`flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:bg-elevated/60 hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border font-mono text-[10px] ${
                    active
                      ? 'border-amber bg-amber text-ink'
                      : done
                      ? 'border-amber/40 text-amber'
                      : 'border-line text-muted'
                  }`}
                >
                  {done ? '✓' : i + 1}
                </span>
                {it.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
